import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  CircularProgress,
  Button,
  Paper,
  IconButton,
  MenuItem,
  Checkbox,
  ListItemText,
  Autocomplete,
  Chip,
} from "@mui/material";
import { Visibility, VisibilityOff, Print, FileDownload, FilterAltOff } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import ReportSummaryCard from "../components/Reports/ReportSummaryCard";
import StandardPagination from "../components/Pagination/StandardPagination";
import { FooterInternal } from "../components/Footer";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  patientSchedulesReportService,
  reportsService,
  PatientScheduleItem,
  PatientScheduleDetailItem,
  ReportPatientItem,
  ReportProfessionalItem,
} from "../services/reportsService";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import { maskName } from "../utils/nameMask";
import MonthSelect from "../components/Inputs/MonthSelect";

// Pacientes × Atendimentos × Profissionais — quantidades por status, valores e
// pagamento por agendamento (qtde/valor pagos e pendentes sobre as presenças cobráveis).

const formatCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const currentCompetence = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const competenceBounds = (competence: string): { from: string; to: string } => {
  const [year, month] = competence.split("-").map((v) => parseInt(v, 10));
  const lastDay = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, "0");
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(lastDay).padStart(2, "0")}` };
};


const filterCardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1.5rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
  mb: 3,
};

const actionButtonSx = {
  padding: "0.5rem 1rem",
  borderRadius: "4px",
  fontSize: "0.85rem",
  fontWeight: typography.fontWeight.semibold,
  textTransform: "none" as const,
  boxShadow: "none",
  backgroundColor: colors.primary,
  color: "#fff",
  "&:hover": { backgroundColor: "#029AAB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#e9ecef", color: "#adb5bd" },
};

const actionButtonGraySx = {
  ...actionButtonSx,
  backgroundColor: "#6c757d",
  "&:hover": { backgroundColor: "#5a6268", boxShadow: "none" },
};

const clearFilterButtonSx = {
  backgroundColor: "#6c757d",
  color: "white",
  width: "40px",
  height: "40px",
  borderRadius: "4px",
  transition: "all 0.2s ease",
  "&:hover": { backgroundColor: "#5a6268" },
  "&:disabled": { backgroundColor: "#e9ecef", color: "#adb5bd", opacity: 0.6 },
};

const sectionStyle = {
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  mb: 3,
};

const ReportPatientSchedules: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const initialBounds = competenceBounds(currentCompetence());
  const [competence, setCompetence] = useState<string>(currentCompetence());
  const [fromDate, setFromDate] = useState<string>(initialBounds.from);
  const [toDate, setToDate] = useState<string>(initialBounds.to);
  const [selectedProfessionals, setSelectedProfessionals] = useState<string[]>([]);
  const [showInactiveProfessionals, setShowInactiveProfessionals] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState<ReportPatientItem[]>([]);
  const [showNames, setShowNames] = useState<boolean>(false);

  const [professionals, setProfessionals] = useState<ReportProfessionalItem[]>([]);
  const visibleProfessionals = professionals.filter((p) => (p.active ?? true) || showInactiveProfessionals);
  const [patients, setPatients] = useState<ReportPatientItem[]>([]);
  const [data, setData] = useState<PatientScheduleItem[] | null>(null);
  const [details, setDetails] = useState<PatientScheduleDetailItem[]>([]);
  const [view, setView] = useState<"consolidado" | "analitico">("consolidado");
  const [pendingFilter, setPendingFilter] = useState<"todos" | "pendentes" | "sem">("todos");
  const [detailPage, setDetailPage] = useState<number>(1);
  const [detailPerPage, setDetailPerPage] = useState<number>(25);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const displayName = (name: string): string => (showNames ? name : maskName(name));

  const defaultBounds = competenceBounds(currentCompetence());
  const hasFilterChanges =
    competence !== currentCompetence() ||
    fromDate !== defaultBounds.from ||
    toDate !== defaultBounds.to ||
    selectedProfessionals.length > 0 ||
    selectedPatients.length > 0 ||
    pendingFilter !== "todos";

  const loadData = useCallback(
    async (override?: {
      from?: string;
      to?: string;
      professionalIds?: string[];
      patientIds?: string[];
    }) => {
      const from = override?.from ?? fromDate;
      const to = override?.to ?? toDate;
      if (!from || !to) return;
      setLoading(true);
      setError("");
      try {
        const params = {
          from,
          to,
          professionalIds: override?.professionalIds ?? selectedProfessionals,
          patientIds: override?.patientIds ?? selectedPatients.map((p) => p.externalId),
        };
        const [summary, analytic] = await Promise.all([
          patientSchedulesReportService.get(params),
          patientSchedulesReportService.getDetails(params),
        ]);
        setData(summary);
        setDetails(analytic);
        setDetailPage(1);
      } catch {
        setError("Não foi possível carregar os dados. Tente novamente.");
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, selectedProfessionals, selectedPatients]
  );

  const handleCompetenceChange = (value: string) => {
    setCompetence(value);
    if (value) {
      const bounds = competenceBounds(value);
      setFromDate(bounds.from);
      setToDate(bounds.to);
      loadData({ from: bounds.from, to: bounds.to });
    }
  };

  const handleFromDateChange = (value: string) => {
    setFromDate(value);
    setCompetence("");
    if (value && toDate) loadData({ from: value, to: toDate });
  };

  const handleToDateChange = (value: string) => {
    setToDate(value);
    setCompetence("");
    if (fromDate && value) loadData({ from: fromDate, to: value });
  };

  const toggleInactiveProfessionals = () => {
    setShowInactiveProfessionals((prev) => {
      const next = !prev;
      if (!next) {
        const onlyActive = selectedProfessionals.filter(
          (id) => professionals.find((p) => p.externalId === id)?.active ?? true,
        );
        if (onlyActive.length !== selectedProfessionals.length) {
          setSelectedProfessionals(onlyActive);
          loadData({ professionalIds: onlyActive });
        }
      }
      return next;
    });
  };

  const handleProfessionalsChange = (values: string[]) => {
    // "__all__" alterna selecionar/desselecionar todos (padrão da Agenda)
    if (values.includes("__all__")) {
      const all = selectedProfessionals.length === visibleProfessionals.length
        ? []
        : visibleProfessionals.map((p) => p.externalId);
      setSelectedProfessionals(all);
      loadData({ professionalIds: all });
      return;
    }

    setSelectedProfessionals(values);
    loadData({ professionalIds: values });
  };

  const handlePatientsChange = (values: ReportPatientItem[]) => {
    setSelectedPatients(values);
    loadData({ patientIds: values.map((p) => p.externalId) });
  };

  const clearFilters = () => {
    const competenceNow = currentCompetence();
    const bounds = competenceBounds(competenceNow);
    setCompetence(competenceNow);
    setFromDate(bounds.from);
    setToDate(bounds.to);
    setSelectedProfessionals([]);
    setSelectedPatients([]);
    setPendingFilter("todos");
    loadData({ from: bounds.from, to: bounds.to, professionalIds: [], patientIds: [] });
  };

  const handlePrint = () => {
    // impressão sempre com nomes mascarados, independentemente do estado da tela
    const wasShowing = showNames;
    setShowNames(false);
    setTimeout(() => {
      window.print();
      setShowNames(wasShowing);
    }, 150);
  };

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const blob = await patientSchedulesReportService.exportXlsx({
        from: fromDate,
        to: toDate,
        professionalIds: selectedProfessionals,
        patientIds: selectedPatients.map((p) => p.externalId),
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pacientes_atendimentos_${fromDate}_a_${toDate}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar a planilha. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    reportsService.getProfessionals().then(setProfessionals).catch(() => setProfessionals([]));
    patientSchedulesReportService.getPatients().then(setPatients).catch(() => setPatients([]));
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredData = (data ?? []).filter((r) =>
    pendingFilter === "todos"
      ? true
      : pendingFilter === "pendentes"
      ? r.pendingValue >= 0.01
      : r.pendingValue < 0.01
  );
  const filteredDetails = details.filter((r) =>
    pendingFilter === "todos"
      ? true
      : pendingFilter === "pendentes"
      ? r.paymentStatus === "Pendente"
      : r.paymentStatus !== "Pendente"
  );

  const totals = filteredData.reduce(
    (acc, r) => ({
      scheduled: acc.scheduled + r.scheduled,
      attended: acc.attended + r.attended,
      paidCount: acc.paidCount + r.paidCount,
      paidValue: acc.paidValue + r.paidValue,
      pendingCount: acc.pendingCount + r.pendingCount,
      pendingValue: acc.pendingValue + r.pendingValue,
      totalValue: acc.totalValue + r.totalValue,
    }),
    { scheduled: 0, attended: 0, paidCount: 0, paidValue: 0, pendingCount: 0, pendingValue: 0, totalValue: 0 }
  );

  const headerCellSx: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: "#6c757d",
    textTransform: "uppercase",
    padding: "0.5rem 0.6rem",
    whiteSpace: "nowrap",
    textAlign: "right",
  };

  const numberCellSx: React.CSSProperties = {
    fontSize: 13,
    padding: "0.4rem 0.6rem",
    textAlign: "right",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  };

  // cabeçalho das colunas de contagem: rótulo curto e coluna estreita (sobrava espaço)
  const compactHeaderSx: React.CSSProperties = {
    ...headerCellSx,
    padding: "0.5rem 0.35rem",
    fontSize: 11,
  };

  // nomes longos quebram linha em vez de esticar a tabela (e cortar as colunas da direita)
  const nameCellSx: React.CSSProperties = {
    fontSize: 13,
    padding: "0.4rem 0.6rem",
    overflowWrap: "anywhere",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          /* tabela larga: paisagem e sem scroll horizontal, para nada sair cortado */
          @page { size: A4 landscape; margin: 8mm; }
          [style*="overflow-x"], [style*="overflowX"] { overflow: visible !important; }
          table { min-width: 0 !important; width: 100% !important; font-size: 10px !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="h4" className="c4u-page-header__title">
                Relatórios / Pacientes × Atendimentos
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Uma linha por paciente e profissional. Pagamento por agendamento sobre as presenças
                cobráveis (status de presença marcados como "cobra atendimento"); valor pago usa o
                valor final do pagamento.
              </Typography>
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          {/* Filtros e ações */}
          <Paper className="no-print" elevation={0} sx={filterCardSx}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: "0.75rem",
                alignItems: { xs: "stretch", sm: "flex-start" },
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "0.75rem",
                  flex: 1,
                }}
              >
                <MonthSelect
                  label="Competência"
                  value={competence}
                  onChange={handleCompetenceChange}
                  sx={filterFieldSx}
                />
                <TextField
                  size="small"
                  type="date"
                  label="De"
                  value={fromDate}
                  onChange={(e) => handleFromDateChange(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Até"
                  value={toDate}
                  onChange={(e) => handleToDateChange(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <Autocomplete
                  multiple
                  size="small"
                  options={patients}
                  value={selectedPatients}
                  onChange={(_, values) => handlePatientsChange(values)}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.externalId === value.externalId}
                  limitTags={1}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Pacientes"
                      placeholder={selectedPatients.length === 0 ? "Todos" : ""}
                      sx={filterFieldSx}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
                <TextField
                  select
                  size="small"
                  label="Profissionais"
                  value={selectedProfessionals}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleProfessionalsChange(typeof value === "string" ? value.split(",") : value);
                  }}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => {
                      const ids = selected as string[];
                      if (ids.length === 0) return "Todos";
                      return `${ids.length} selecionado(s)`;
                    },
                    displayEmpty: true,
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="__all__">
                    <Checkbox
                      size="small"
                      checked={visibleProfessionals.length > 0 && selectedProfessionals.length === visibleProfessionals.length}
                      indeterminate={
                        selectedProfessionals.length > 0 &&
                        selectedProfessionals.length < visibleProfessionals.length
                      }
                    />
                    <ListItemText primary="Selecionar todos" primaryTypographyProps={{ fontSize: 14 }} />
                  </MenuItem>
                  {/* Inativos sob demanda, como na Agenda: histórico de quem saiu */}
                  <MenuItem
                    value="__inactive"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleInactiveProfessionals();
                    }}
                    sx={{
                      backgroundColor: "#f8f9fa !important",
                      fontSize: "0.85rem",
                      borderBottom: "1px solid #e9ecef",
                    }}
                  >
                    <Checkbox
                      checked={showInactiveProfessionals}
                      sx={{ padding: "4px 8px 4px 4px", "& .MuiSvgIcon-root": { fontSize: "1.2rem" } }}
                    />
                    <ListItemText primary="Mostrar inativos" primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }} />
                  </MenuItem>
                  {visibleProfessionals.map((p) => (
                    <MenuItem key={p.externalId} value={p.externalId}>
                      <Checkbox size="small" checked={selectedProfessionals.includes(p.externalId)} />
                      <ListItemText primary={p.name + (p.active === false ? " (inativo)" : "")} primaryTypographyProps={{ fontSize: 14 }} />
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Valor pendente"
                  value={pendingFilter}
                  onChange={(e) => {
                    setPendingFilter(e.target.value as "todos" | "pendentes" | "sem");
                    setDetailPage(1);
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="pendentes">Pagamento pendente</MenuItem>
                  <MenuItem value="sem">Sem pendências</MenuItem>
                </TextField>
              </Box>
              <IconButton
                onClick={clearFilters}
                disabled={!hasFilterChanges || loading}
                title="Limpar filtros"
                sx={{ ...clearFilterButtonSx, flexShrink: 0, alignSelf: { xs: "flex-end", sm: "auto" } }}
              >
                <FilterAltOff fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: { sm: "flex-end" },
                flexWrap: { sm: "wrap" },
                gap: "0.5rem",
                // no celular cada ação ocupa a largura toda (alvo de toque confortável)
                "& .MuiButton-root": {
                  flexShrink: 0,
                  minHeight: 44,
                },
              }}
            >
              <Button
                variant="contained"
                startIcon={showNames ? <VisibilityOff /> : <Visibility />}
                onClick={() => setShowNames((v) => !v)}
                sx={actionButtonGraySx}
              >
                {showNames ? "Ocultar nomes" : "Exibir nomes"}
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={loading || !data}
                sx={actionButtonGraySx}
              >
                Imprimir
              </Button>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExport}
                disabled={loading || exporting || !data}
                sx={actionButtonSx}
              >
                {exporting ? "Exportando..." : "Exportar planilha"}
              </Button>
            </Box>
          </Paper>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#dc3545" }}>{error}</Box>
          )}

          {!loading && !error && data && data.length === 0 && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#6c757d" }}>
              Nenhum atendimento encontrado para os filtros selecionados.
            </Box>
          )}

          {!loading && !error && data && data.length > 0 && filteredData.length === 0 && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#6c757d" }}>
              Nenhuma combinação atende ao filtro de valor pendente selecionado.
            </Box>
          )}

          {!loading && !error && data && data.length > 0 && filteredData.length > 0 && (() => {
            // Dashboard: agregados de pagamento (totals do componente) sobre o filtro atual
            // identidade: realizado (valor de agenda) = pago (líquido) + pendente + descontos concedidos
            const discountValue = Math.max(totals.totalValue - totals.paidValue - totals.pendingValue, 0);
            const paidPercent = totals.totalValue > 0 ? (totals.paidValue / totals.totalValue) * 100 : 0;
            const pendingPercent = totals.totalValue > 0 ? (totals.pendingValue / totals.totalValue) * 100 : 0;
            const discountPercent = Math.max(100 - paidPercent - pendingPercent, 0);

            const pendingByPatient = Object.values(
              filteredData.reduce<Record<string, { name: string; count: number; value: number }>>((acc, r) => {
                if (r.pendingValue <= 0 && r.pendingCount <= 0) return acc;
                const key = r.patientExternalId || r.patientName;
                if (!acc[key]) acc[key] = { name: r.patientName, count: 0, value: 0 };
                acc[key].count += r.pendingCount;
                acc[key].value += r.pendingValue;
                return acc;
              }, {})
            )
              // agendamentos com valor zerado no cadastro do Bubble geram pendência de
              // quantidade sem valor — fora da lista (só pendência financeira real)
              .filter((row) => row.value >= 0.01)
              .sort((a, b) => b.value - a.value)
              .slice(0, 8);

            // pendências financeiras reais x agendamentos pendentes com valor zerado no Bubble
            const pendingFinCount = filteredDetails.filter(
              (r) => r.paymentStatus === "Pendente" && r.finalValue >= 0.01
            ).length;
            const pendingZeroCount = filteredDetails.filter(
              (r) => r.paymentStatus === "Pendente" && r.finalValue < 0.01
            ).length;
            const discountedCount = filteredDetails.filter(
              (r) => r.paymentStatus === "Pago" && r.discount >= 0.01
            ).length;

            const summaryCards = [
              { label: "Realizado Bruto", count: totals.paidCount + totals.pendingCount, value: totals.totalValue, color: "#2D3748", hint: "presenças cobráveis (valor de agenda)", note: "" },
              {
                label: "Descontos",
                count: discountedCount,
                value: discountValue,
                color: "#fd7e14",
                hint: "atendimentos com desconto",
                note: "",
              },
              {
                label: "Realizado Líquido",
                count: totals.paidCount + totals.pendingCount,
                value: totals.totalValue - discountValue,
                color: "#03B4C6",
                hint: "bruto − descontos",
                note: "",
              },
              { label: "Pagos", count: totals.paidCount, value: totals.paidValue, color: "#28a745", hint: "atendimentos quitados", note: "" },
              {
                label: "Pendentes",
                count: pendingFinCount,
                value: totals.pendingValue,
                color: "#dc3545",
                hint: "aguardando pagamento",
                note: pendingZeroCount > 0 ? `+${pendingZeroCount} sem valor cadastrado` : "",
              },
            ];

            return (
            <>
            <Box sx={{ ...sectionStyle }}>
              <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 2 }}>
                Realizado × Pago × Pendente
              </Typography>
              <Box sx={{ display: "flex", gap: 3, alignItems: "stretch", flexWrap: "wrap" }}>
                {/* donut pago x pendente (em valor) */}
                <Box sx={{ position: "relative", width: 140, height: 140, flexShrink: 0, alignSelf: "center", mx: { xs: "auto", md: 0 } }}>
                  <svg width="140" height="140" viewBox="0 0 42 42" style={{ transform: "rotate(-90deg)" }}>
                    <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#e9ecef" strokeWidth="3" />
                    {totals.totalValue > 0 && (
                      <>
                        <circle
                          cx="21" cy="21" r="15.915" fill="transparent"
                          stroke="#28a745" strokeWidth="3"
                          strokeDasharray={`${paidPercent} ${100 - paidPercent}`}
                          strokeDashoffset={0}
                          strokeLinecap="round"
                        />
                        <circle
                          cx="21" cy="21" r="15.915" fill="transparent"
                          stroke="#dc3545" strokeWidth="3"
                          strokeDasharray={`${pendingPercent} ${100 - pendingPercent}`}
                          strokeDashoffset={-paidPercent}
                          strokeLinecap="round"
                        />
                        {discountPercent > 0.5 && (
                          <circle
                            cx="21" cy="21" r="15.915" fill="transparent"
                            stroke="#fd7e14" strokeWidth="3"
                            strokeDasharray={`${discountPercent} ${100 - discountPercent}`}
                            strokeDashoffset={-(paidPercent + pendingPercent)}
                            strokeLinecap="round"
                          />
                        )}
                      </>
                    )}
                  </svg>
                  <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: "#28a745" }}>
                      {paidPercent.toFixed(0)}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      pago
                    </Typography>
                  </Box>
                </Box>

                {/* cards realizado / pago / pendente */}
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: 1, minWidth: 260 }}>
                  {summaryCards.map((card) => (
                    <ReportSummaryCard
                      key={card.label}
                      label={card.label}
                      value={formatCurrency(card.value)}
                      color={card.color}
                      sub={`${card.count} ${card.hint}`}
                      note={card.note || undefined}
                    />
                  ))}
                </Box>
              </Box>

              {/* maiores pendências por paciente */}
              {pendingByPatient.length > 0 && (
                <Box sx={{ mt: 2, overflowX: "auto" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: typography.fontWeight.semibold, color: "#6c757d", mb: 1 }}>
                    Maiores pendências por paciente
                  </Typography>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid #e9ecef" }}>
                        <th style={{ ...headerCellSx, textAlign: "left" }}>Paciente</th>
                        <th style={headerCellSx}>Qtde pendente</th>
                        <th style={headerCellSx}>Valor pendente</th>
                        <th style={{ ...headerCellSx, textAlign: "left", width: "30%" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingByPatient.map((row, index) => (
                        <tr key={index} style={{ borderBottom: "1px solid #f1f3f5" }}>
                          <td style={nameCellSx}>{displayName(row.name)}</td>
                          <td style={numberCellSx}>{row.count}</td>
                          <td style={{ ...numberCellSx, fontWeight: 600, color: "#dc3545" }}>
                            {formatCurrency(row.value)}
                          </td>
                          <td style={{ padding: "0.4rem 0.75rem" }}>
                            <Box sx={{ background: "#f1f3f5", borderRadius: 1, height: 8, overflow: "hidden" }}>
                              <Box
                                sx={{
                                  width: `${totals.pendingValue > 0 ? Math.max((row.value / totals.pendingValue) * 100, 2) : 0}%`,
                                  background: "#dc3545",
                                  height: "100%",
                                }}
                              />
                            </Box>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </Box>

            <Box sx={{ ...sectionStyle, p: 0, overflowX: "auto" }}>
              {/* alternância entre a visão consolidada e a analítica (agendamento a agendamento) */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid #e9ecef",
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, flex: 1 }}>
                  {view === "consolidado"
                    ? "Consolidado por paciente e profissional"
                    : `Analítico — ${filteredDetails.length} atendimentos`}
                </Typography>
                {(["consolidado", "analitico"] as const).map((option) => (
                  <Chip
                    key={option}
                    size="small"
                    label={option === "consolidado" ? "Consolidado" : "Analítico"}
                    onClick={() => setView(option)}
                    className="no-print"
                    sx={{
                      cursor: "pointer",
                      backgroundColor: view === option ? colors.primary : "#f1f3f5",
                      color: view === option ? "#fff" : "#495057",
                      fontWeight: typography.fontWeight.semibold,
                    }}
                  />
                ))}
              </Box>

              {view === "analitico" && (
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e9ecef" }}>
                      <th style={{ ...headerCellSx, textAlign: "left" }}>Paciente</th>
                      <th style={{ ...headerCellSx, textAlign: "left" }}>Data</th>
                      <th style={{ ...headerCellSx, textAlign: "left" }}>Início</th>
                      <th style={{ ...headerCellSx, textAlign: "left" }}>Fim</th>
                      <th style={{ ...headerCellSx, textAlign: "left" }}>Profissional</th>
                      <th style={{ ...headerCellSx, textAlign: "left" }}>Especialidade</th>
                      <th style={{ ...headerCellSx, textAlign: "left" }}>Pagamento</th>
                      <th style={headerCellSx}>Valor normal</th>
                      <th style={headerCellSx}>Desconto</th>
                      <th style={headerCellSx}>Valor final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDetails
                      .slice((detailPage - 1) * detailPerPage, detailPage * detailPerPage)
                      .map((r, index) => (
                      <tr key={index} style={{ borderBottom: "1px solid #f1f3f5" }}>
                        <td style={nameCellSx}>{displayName(r.patientName)}</td>
                        <td style={nameCellSx}>{r.date}</td>
                        <td style={nameCellSx}>{r.startTime}</td>
                        <td style={nameCellSx}>{r.endTime}</td>
                        <td style={nameCellSx}>{displayName(r.professionalName)}</td>
                        <td style={nameCellSx}>{r.specialty}</td>
                        <td
                          style={{
                            ...nameCellSx,
                            fontWeight: 600,
                            color:
                              r.paymentStatus === "Pago"
                                ? "#28a745"
                                : r.paymentStatus === "Pendente"
                                ? "#dc3545"
                                : "#adb5bd",
                          }}
                        >
                          {r.paymentStatus}
                        </td>
                        <td style={numberCellSx}>{formatCurrency(r.normalValue)}</td>
                        <td style={numberCellSx}>{formatCurrency(r.discount)}</td>
                        <td style={{ ...numberCellSx, fontWeight: 600 }}>{formatCurrency(r.finalValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "2px solid #e9ecef", background: "#f8f9fa" }}>
                      <td style={{ ...nameCellSx, fontWeight: 600 }} colSpan={7}>
                        Total ({filteredDetails.length} atendimentos)
                      </td>
                      <td style={{ ...numberCellSx, fontWeight: 600 }}>
                        {formatCurrency(filteredDetails.reduce((acc, r) => acc + r.normalValue, 0))}
                      </td>
                      <td style={{ ...numberCellSx, fontWeight: 600 }}>
                        {formatCurrency(filteredDetails.reduce((acc, r) => acc + r.discount, 0))}
                      </td>
                      <td style={{ ...numberCellSx, fontWeight: 600 }}>
                        {formatCurrency(filteredDetails.reduce((acc, r) => acc + r.finalValue, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}

              {view === "analitico" && filteredDetails.length > detailPerPage && (
                <Box className="no-print">
                  <StandardPagination
                    currentPage={detailPage}
                    totalPages={Math.ceil(filteredDetails.length / detailPerPage)}
                    itemsPerPage={detailPerPage}
                    totalItems={filteredDetails.length}
                    onPageChange={setDetailPage}
                    onItemsPerPageChange={(value) => {
                      setDetailPerPage(value);
                      setDetailPage(1);
                    }}
                  />
                </Box>
              )}

              {view === "consolidado" && (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: 900,
                  tableLayout: "fixed",
                }}
              >
                {/* Colunas Canc. profissional, Sem status, Valor unit. e Qtde paga ficam só no
                    export (.xlsx) para manter a tela dentro da largura.
                    Larguras fixas: as contagens não precisam de mais que 64–76px (sobrava
                    espaço) e os nomes ficam com o que resta, quebrando linha em vez de cortar. */}
                <colgroup>
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: 64 }} />
                  <col style={{ width: 76 }} />
                  <col style={{ width: 72 }} />
                  <col style={{ width: 104 }} />
                  <col style={{ width: 64 }} />
                  <col style={{ width: 104 }} />
                  <col style={{ width: 110 }} />
                </colgroup>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e9ecef" }}>
                    <th style={{ ...headerCellSx, textAlign: "left" }}>Paciente</th>
                    <th style={{ ...headerCellSx, textAlign: "left" }}>Profissional</th>
                    <th style={{ ...headerCellSx, textAlign: "left" }}>Especialidade</th>
                    <th style={compactHeaderSx} title="Agendados">Agend.</th>
                    <th style={compactHeaderSx} title="Cancelados pelo paciente">Canc. pac.</th>
                    <th style={compactHeaderSx} title="Realizados">Realiz.</th>
                    <th style={headerCellSx}>Valor pago</th>
                    <th style={compactHeaderSx} title="Quantidade pendente">Qtde pend.</th>
                    <th style={headerCellSx}>Valor pend.</th>
                    <th style={headerCellSx}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((r) => (
                    <tr
                      key={`${r.patientExternalId}-${r.professionalExternalId}`}
                      style={{ borderBottom: "1px solid #f1f3f5" }}
                    >
                      <td style={nameCellSx}>{displayName(r.patientName)}</td>
                      <td style={nameCellSx}>{displayName(r.professionalName)}</td>
                      <td style={nameCellSx}>{r.specialty}</td>
                      <td style={numberCellSx}>{r.scheduled}</td>
                      <td style={numberCellSx}>{r.cancelledByPatient}</td>
                      <td style={numberCellSx}>{r.attended}</td>
                      <td style={numberCellSx}>{formatCurrency(r.paidValue)}</td>
                      <td style={{ ...numberCellSx, color: r.pendingCount > 0 ? "#dc3545" : undefined }}>
                        {r.pendingCount}
                      </td>
                      <td style={{ ...numberCellSx, color: r.pendingValue > 0 ? "#dc3545" : undefined }}>
                        {formatCurrency(r.pendingValue)}
                      </td>
                      <td style={{ ...numberCellSx, fontWeight: 600 }}>{formatCurrency(r.totalValue)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "2px solid #e9ecef", background: "#f8f9fa" }}>
                    <td style={{ ...nameCellSx, fontWeight: 600 }} colSpan={3}>
                      Total ({filteredData.length} combinações)
                    </td>
                    <td style={{ ...numberCellSx, fontWeight: 600 }}>{totals.scheduled}</td>
                    <td style={numberCellSx} />
                    <td style={{ ...numberCellSx, fontWeight: 600 }}>{totals.attended}</td>
                    <td style={{ ...numberCellSx, fontWeight: 600 }}>{formatCurrency(totals.paidValue)}</td>
                    <td style={{ ...numberCellSx, fontWeight: 600, color: totals.pendingCount > 0 ? "#dc3545" : undefined }}>
                      {totals.pendingCount}
                    </td>
                    <td style={{ ...numberCellSx, fontWeight: 600, color: totals.pendingValue > 0 ? "#dc3545" : undefined }}>
                      {formatCurrency(totals.pendingValue)}
                    </td>
                    <td style={{ ...numberCellSx, fontWeight: 600 }}>{formatCurrency(totals.totalValue)}</td>
                  </tr>
                </tfoot>
              </table>
              )}
            </Box>
            </>
            );
          })()}
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportPatientSchedules;
